/**
 * Messages with markup: `<b>…</b>`-style tags become React elements from a
 * render function, and `{param}` may be any React node. Tags do not nest
 * across each other; a tag's content is itself formatted, so params inside a
 * tag work.
 */
import { Fragment, createElement, type ReactNode } from "react";

import { lowerFirst } from "./lower-first";

export type RichValues = {
  readonly [name: string]: ReactNode | ((chunks: ReactNode) => ReactNode);
};

const TOKEN = /<([a-z][a-z0-9]*)>([\s\S]*?)<\/\1>|\{([^}|]+)(?:\|(lower))?\}/g;

export function formatRich(text: string, values: RichValues): ReactNode {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(TOKEN)) {
    const [whole, tag, inner, param, modifier] = match;
    const index = match.index ?? 0;
    if (index > last) parts.push(text.slice(last, index));
    if (tag !== undefined) {
      const render = values[tag];
      const content = formatRich(inner ?? "", values);
      parts.push(typeof render === "function" ? render(content) : content);
    } else if (param !== undefined) {
      const value = values[param];
      if (value === undefined || typeof value === "function") parts.push(whole);
      else if (modifier === "lower" && typeof value === "string")
        parts.push(lowerFirst(value));
      else parts.push(value);
    }
    last = index + whole.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1
    ? parts[0]
    : createElement(
        Fragment,
        null,
        ...parts.map((part, key) => createElement(Fragment, { key }, part)),
      );
}
